import { SURVEY_TYPE, SURVEY_RESTRICTION_METHOD, SURVEY_QUESTION_TYPE } from '../../domain/domainCodes.js'
/**
 * Legacy strings for the four engagement screens — Locale/Awareness,
 * Admin/WorkspacePoliciesSettings, Admin/SurveysManagement and
 * Admin/AssessmentsManagement.
 */

/**
 * Shared across all four. Status and mandatory wording is NOT shared — each screen
 * has its own pair, which is why they sit on the individual objects below.
 */
const shared = {
  close: 'إغلاق', // Users.btnClose
  previousNotifications: 'التنبيهات السابقة', // Resources.PreviousNotifications
  /* THE TITLE ASKS, THE MESSAGE EXPLAINS. Both dialogs used to open with the kit's
     default heading — `تأكيد الحذف` — over a sentence about showing or hiding something,
     which read as a delete prompt that had lost its message. */
  deactivateTitle: 'إلغاء تفعيل هذا المحتوى؟',
  activateTitle: 'تفعيل هذا المحتوى؟',
  deactivateConfirm: 'لن يظهر للمتدربين بعد الآن، ويمكن تفعيله مرة أخرى في أي وقت.',
  activateConfirm: 'سيظهر للمتدربين مباشرة.',
  saved: 'تم الحفظ بنجاح',
  activated: 'تم التفعيل',
  deactivated: 'تم إلغاء التفعيل',
  actionFailed: 'تعذر إتمام العملية',
}

/** Shared summary labels — the four engagement lists have the same shape. */
export const engagementStats = {
  caption: 'ملخص',
  active: 'نشط',
  inactive: 'غير مفعلة',
}

export const awarenessStrings = {
  ...shared,
  fail: {
    list: 'تعذر تحميل المحتوى التوعوي',
    save: 'تعذر حفظ المحتوى التوعوي',
    delete: 'تعذر حذف المحتوى التوعوي',
    activate: 'تعذر تغيير حالة المحتوى التوعوي',
    export: 'تعذر تصدير المحتوى التوعوي',
    settings: 'تعذر حفظ إعدادات المحتوى التوعوي',
  },
  title: 'النشرات التوعوية', // Awareness.Awarenesses
  /* بطاقة نطاق لا مجرد عداد - `engagementStats.total` مشترك مع بقية القوائم. */
  allContent: 'كل النشرات',
  subtitle: 'محرر', // Awareness.Editor
  create: 'إضافة نشرة توعوية', // Awareness.New_Awareness_Content

  columns: {
    title: 'العنوان', // Awareness.Title
    titleEn: 'العنوان باللغة الإنجليزية', // Awareness.TitleEN
    user: 'المستخدم', // Awareness.User
    createdOn: 'تاريخ الإنشاء', // Awareness.Creation
    isActive: 'نشط', // Awareness.IsActive
  },

  active: 'نشط', // Resources.ACTIVEE
  inactive: 'غير مفعلة', // Resources.InActive

  row: {
    exportPackage: 'تصدير حزمة المستأجر',
    deactivate: 'إلغاء التفعيل',
    activate: 'تفعيل', edit: 'تعديل', delete: 'حذف',
    views: 'من اطلع' },

  /* `POST .../views` answers a page of everyone who opened a bulletin and had no caller at
     all, so an administrator could not tell whether a bulletin had been read. */
  views: {
    title: 'من اطلع على النشرة',
    empty: 'لم يطلع أحد على هذه النشرة بعد',
    failed: 'تعذر تحميل قائمة المطلعين',
    columns: {
      name: 'الاسم',
      username: 'اسم المستخدم',
      email: 'البريد الإلكتروني',
      seenOn: 'تاريخ الاطلاع',
    },
  },
  /** The نشرة form — Views/Awareness `#edit_form`. */
  form: {
    // Read by the form and never defined — a successful save showed an empty toast.
    saved: 'تمت العملية بنجاح', // Resources.Operation_Completed_Successfully
    titleAr: 'العنوان العربي', // Awareness.Title
    contentAr: 'المحتوى باللغة العربية',
    titleEn: 'العنوان باللغة الإنجليزية', // Awareness.TitleEN
    contentEn: 'المحتوى باللغة الإنجليزية',
    cover: 'صورة الغلاف',
    state: 'الحالة',
    isActive: 'نشط', // Awareness.IsActive
    /* The three steps this form is split into. The legacy is one long modal
       plus two satellite modals; these are its own three subjects. */
    steps: {
      content: 'محتوى النشرة',
      media: 'الوسائط والمرفقات',
      audience: 'النشر والجمهور',
    },
    videos: 'مقاطع الفيديو', // CategoriesManagement.VideoUploadFileRegex_Text
    addVideo: 'رفع مقطع',
    noVideos: 'لم يتم رفع مقاطع',
    attachments: 'المرفقات', // Awareness.Attachments
    addAttachment: 'إضافة مرفق',
    noAttachments: 'لا توجد مرفقات',
    /* The bulletin saved and an attachment did not — two different outcomes, and the
       reader needs to know the first one held. */
    attachmentsFailed: 'تم حفظ النشرة، ولكن تعذر حفظ أحد المرفقات',
    attachmentNameAr: 'اسم المرفق بالعربية', // CoursesManagement.AttachmentsArabicName
    attachmentNameEn: 'اسم المرفق بالإنجليزية', // CoursesManagement.AttachmentsEnglishName
    fileAr: 'الملف بالعربية', // CoursesManagement.FileAr
    fileEn: 'الملف بالإنجليزية', // CoursesManagement.FileEn
    remove: 'حذف',
    groups: 'تخصيص المحتوى للإدارات التالية',
    selectAll: 'إختيار الكل',
    filter: 'تصفية',
    save: 'حفظ',
    required: 'الحقل مطلوب',
  },

  deleteConfirm: 'هل تريد حذف هذه النشرة؟',
  deleted: 'تمت العملية بنجاح', // Operation_Completed_Successfully
  /** إعدادات شاشة التوعية — `save-settings`. */
  settings: {
    tab: 'الإعدادات',
    contentTab: 'النشرات',
    screen: 'شاشة التوعية',
    screenHint: 'تفعيل الشاشة لدى المتدربين والاسم الذي تظهر به',
    enabled: 'إظهار شاشة التوعية للمتدربين',
    enabledHint: 'عند الإيقاف لا تظهر الشاشة في قائمة المتدرب',
    nameAr: 'اسم الشاشة بالعربية',
    nameEn: 'اسم الشاشة بالإنجليزية',
    nameHint: 'الاسم الذي يظهر في قائمة المتدرب',
    disabledNote: 'سيتم إخفاء شاشة التوعية عن جميع المتدربين عند الحفظ.',
    notification: 'التنبيهات',
    notificationHint: 'تنبيه المتدربين بالنشرات الجديدة',
    notifyOn: 'مفعل',
    notifyOff: 'غير مفعل',
    save: 'حفظ الإعدادات',
    saving: 'فضلا انتظر',
    saved: 'تم حفظ الإعدادات بنجاح',
  },

}

export const policiesStrings = {
  ...shared,
  fail: {
    list: 'تعذر تحميل السياسات',
    save: 'تعذر حفظ السياسة',
    delete: 'تعذر حذف السياسة',
    categories: 'تعذر تحميل تصنيفات السياسات',
    saveCategory: 'تعذر حفظ التصنيف',
    deleteCategory: 'تعذر حذف التصنيف',
    settings: 'تعذر حفظ إعدادات السياسات',
  },
  title: 'إدارة سياسات الأمن السيبراني', // WorkspacePoliciesSettings.PanelHeading
  /* بطاقة نطاق لا مجرد عداد - `engagementStats.total` مشترك مع بقية القوائم. */
  allPolicies: 'كل السياسات',
  subtitle: 'إضافة أو تعديل أو حذف السياسات الأمن السيبراني', // PanelSubHeading
  create: 'سياسة جديدة', // btnNew

  enableFeature: 'تفعيل خاصية السياسات', // WorkspacePoliciesSettings.LocChkBxEnable

  /** The screen's two tabs, as in the legacy; the categories sit under the settings. */
  sections: {
    policies: 'السياسات',
    settings: 'الإعدادات',
  },

  state: {
    activate: 'تفعيل', // LblPolicyStateEnabled
    deactivate: 'إيقاف', // LblPolicyStateDisabled
  },

  /** تصنيفات السياسات — PolicyCategoryModal.cshtml. */
  categories: {
    title: 'تصنيفات السياسات',
    add: 'تصنيف جديد',
    edit: 'تعديل التصنيف',
    nameAr: 'الاسم بالعربية',
    nameEn: 'الاسم بالإنجليزية',
    enabled: 'هذا التصنيف مفعل',
    empty: 'لا توجد تصنيفات بعد',
    deleteConfirm: 'هل تريد حذف هذا التصنيف؟',
  },

  /** إعدادات السياسات — SettingsTab.cshtml. */
  settings: {
    /* `ShowPolicyCategoriesAsGrid`: on, the learner's page is one grid with no category
       sections. */
    viewAsGrid: 'عرض السياسات في شكل مصفوفة بدون تصنيف', // Resources.PoliciesViewType
    pageTitle: 'عنوان صفحة السياسات', // PolicyPageTitleHeader
    pageTitleAr: 'العنوان بالعربية', // spanPolicyTitleAr
    pageTitleEn: 'العنوان بالإنجليزية', // spanPolicyTitleEn
    pageTitleInvalid: 'يرجى التأكد من أن نص عنوان الصفحة يتراوح بين 2 و 200 حرف.', // MessagePageTitleError
    update: 'تعديل', // UpdateButton_Text
    externalUrl: 'رابط نظام السياسات الخارجي', // External_Policies_System_URL
    externalUrlHint: 'عند تعبئته تفتح السياسات من هذا الرابط بدلا من صفحة النظام',
    urlInvalid: 'الرابط غير صحيح', // Resources.invalidUrl
    save: 'حفظ', // Resources.Save
  },
  columns: {
    index: 'م', // TableHead_1
    name: 'الاسم بالعربية', // TableHead_2
    nameEn: 'الاسم بالإنجليزية', // TableHead_3
    version: 'رقم الإصدار', // TableHead_4
    createdOn: 'تاريخ الإنشاء', // TableHead_5
    forceUsers: 'إجبار المستخدمين', // Resources.Force_Users
    /* Renamed: the full sentence held a 239px column open on a table that had
       none to spare, and the column beside it (`إجبار المستخدمين`) already
       establishes what is being enforced. */
    evaluationStartDate: 'تاريخ الإجبار', // Resources.Evaluation_Start_Date
    status: 'الحالة', // TableHead_6
  },

  active: 'نشط', // Resources.ACTIVEE
  inactive: 'غير مفعلة', // Resources.InActive
  yes: 'نعم', // Resources.Yes
  no: 'لا', // Resources.No

  /** The users a policy was issued to — UsersPoliciesDetails/Index.cshtml. */
  users: {
    title: 'المستخدمون',
    open: 'قائمة المستخدمين',
    columns: {
      name: 'المستخدم',
      version: 'رقم الإصدار',
      download: 'حالة التحميل',
      downloadDate: 'تاريخ التحميل',
      accept: 'حالة الموافقة',
      acceptDate: 'تاريخ الموافقة',
    },
    searchPlaceholder: 'المستخدم',
    allUsers: 'الكل',
    viewed: 'من اطلع',
    notViewed: 'من لم يطلع',
    empty: 'لم تسند هذه السياسة إلى أي مستخدم',
    exportExcel: 'تصدير إلى إكسل',
    exportPdf: 'تصدير إلى PDF',
    exportFailed: 'تعذر إنشاء الملف',
  },

  row: {
    edit: 'تعديل', // btnEdit
    sendEmail: 'إرسال بريد', // btnSendEmail
    delete: 'حذف', // btnDelete
    /* The two documents, in the legacy's own words. */
    fileAr: 'تحميل ملف اللغة العربية', // WorkspacePoliciesSettings.LblPolicyArabicFile
    fileEn: 'تحميل ملف اللغة الإنجليزية', // LblPolicyEnglishFile
  },
  /**
   * The سياسة جديدة form — Views/WorkspacePoliciesSettings/Index.cshtml.
   * Every label below is the legacy's own; `readOnly` is its odd one, a select
   * whose two options decide whether a policy is merely READ or has to be
   * acknowledged.
   */
  form: {
    /* Section headings, not legacy labels — the legacy form has none. */
    identity: 'بيانات السياسة',
    /* The third step: who has to read it. */
    audience: 'الفئة المستهدفة',
    documents: 'ملفات السياسة',
    fileTypes: 'PDF',  // SavePolicyHandler accepts nothing else
    forceUsersHint: 'لن يتمكن المستخدم من متابعة استخدام النظام قبل الموافقة عليها',
    nameAr: 'العنوان بالعربية', // TableHead_2
    nameEn: 'العنوان بالإنجليزية', // TableHead_3
    version: 'رقم الإصدار', // TableHead_4
    kind: 'نوع السياسة', // WorkspacePoliciesSettings.PolicyType
    acknowledgement: 'للموافقة', // Resources.Acknowledgement
    readOnly: 'للإطلاع', // Resources.ReadOnly
    category: 'تصنيف السياسة', // WorkspacePoliciesSettings.PolicyCategory
    categoryPlaceholder: 'اختر النوع', // Resources.Select Type
    fileAr: 'رفع ملف اللغة العربية', // WorkspacePoliciesSettings.ArabicFile
    fileEn: 'رفع ملف اللغة الإنجليزية', // WorkspacePoliciesSettings.EnglishFile
    forceUsers: 'إجبار المستخدمين على الموافقة', // Resources.Force_Users
    evaluationStartDate: 'تاريخ بداية الإجبار على الموافقة', // Resources.Evaluation_Start_Date
    groups: 'تخصيص السياسة', // WorkspacePoliciesSettings.PolicyGroups
    selectAll: 'اختيار الكل',
    filter: 'تصفية',
    add: 'إضافة', // AddButton.Text
    save: 'حفظ', // Resources.Save
    notAvailable: 'لا يمكن حفظ السياسات من هذه الشاشة حاليا.',
    required: 'الحقل مطلوب',
    versionInvalid: 'رقم الإصدار يجب أن يكون رقما غير سالب',
  },

  deleteConfirm: 'هل تريد حذف هذه السياسة؟',
  deleted: 'تمت العملية بنجاح', // Operation_Completed_Successfully
}

export const surveysStrings = {
  sections: { list: 'الاستبيانات', settings: 'الإعدادات' },
  ...shared,
  fail: {
    list: 'تعذر تحميل الاستبيانات',
    load: 'تعذر تحميل هذا الاستبيان',
    missing: 'هذا الاستبيان غير موجود، ربما تم حذفه',
    save: 'تعذر حفظ الاستبيان',
    delete: 'تعذر حذف الاستبيان',
    importQuestions: 'تعذر قراءة ملف الأسئلة',
  },
  title: 'إدارة الاستبيانات', // SurveysManagement.PanelHeading
  subtitle: 'إدارة الاستبيانات الخاصة بالنظام', // PanelSubHeading
  create: 'استبيان جديد', // BtnAddNewSurvey
  enableFeature: 'تفعيل خاصية الاستبيانات', // LocChkBxEnable
  activeOnly: 'إظهار الاستبيانات النشطة فقط', // OpnShowActiveSurveyOnly
  allSurveys: 'كل الاستبيانات',

  columns: {
    index: 'م', // TableHead_1
    title: 'العنوان', // TableHead_2
    type: 'نوع الاستبيان', // TableHead_9
    createdOn: 'تاريخ الإنشاء', // TableHead_3
    updatedOn: 'تاريخ التعديل', // TableHead_4
    status: 'الحالة', // TableHead_5
    mandatory: 'الإلزامية', // TableHead_6
    answers: 'إجمالي الإجابات', // TableHead_7
  },

  // GetSurveysHandler.cs:72-88 — surveys use مفعل/معطل and إلزامي/اختياري.
  active: 'مفعل', // SurveysManagement.EnabledString
  inactive: 'معطل', // SurveysManagement.DisabledString
  yes: 'إلزامي', // SurveysManagement.MandatoryString
  no: 'اختياري', // SurveysManagement.NotMandatoryString

  /** SurveyType — General = 1, Course = 2, Program = 3. */
  types: {
    [SURVEY_TYPE.GENERAL]: 'عام', // SurveysManagement.SurveyTypeGeneral
    [SURVEY_TYPE.COURSE]: 'دورة تدريبية', // SurveyTypeCourse
    [SURVEY_TYPE.PROGRAM]: 'برنامج تدريبي', // SurveyTypeProgram
  },

  row: {
    update: 'تعديل', // btnUpdate
    details: 'تفاصيل الاستبيان', // btnDetails
    sendEmail: 'إرسال بريد', // btnSendEmail
    delete: 'حذف', // btnDelete
  },
  /** The استبيان form — Views/SurveysManagement `#mdlSurvey`, a 3-step stepper. */
  form: {
    // Same as the awareness form next door: read, never defined.
    saved: 'تمت العملية بنجاح', // Resources.Operation_Completed_Successfully
    steps: { basics: 'البيانات الأساسية', groups: 'التخصيص', rules: 'قواعد الدخول', questions: 'الأسئلة' },
    /* خطوة الأسئلة — كتلة واحدة يقرؤها هذا النموذج ودرج الاستبيان في صفحة البرنامج. */
    questions: {
      questionAr: 'السؤال بالعربية',
      questionEn: 'السؤال بالإنجليزية',
      questionType: 'نوع السؤال',
      questionTypes: {
        [SURVEY_QUESTION_TYPE.RADIO]: 'اختيار واحد',
        [SURVEY_QUESTION_TYPE.CHECKBOX]: 'اختيار متعدد',
        [SURVEY_QUESTION_TYPE.YES_NO]: 'نعم / لا',
        [SURVEY_QUESTION_TYPE.STARS]: 'تقييم بالنجوم',
        [SURVEY_QUESTION_TYPE.SHORT_TEXT]: 'إجابة قصيرة',
        [SURVEY_QUESTION_TYPE.LONG_TEXT]: 'إجابة طويلة',
      },
      /* ما يطلبه كل نوع من المشارك، تحت القائمة. */
      typeHelp: {
        [SURVEY_QUESTION_TYPE.RADIO]: 'يختار المشارك خياراً واحداً فقط من الخيارات التي تكتبها أدناه.',
        [SURVEY_QUESTION_TYPE.CHECKBOX]: 'يختار المشارك أي عدد من الخيارات التي تكتبها أدناه.',
        [SURVEY_QUESTION_TYPE.YES_NO]: 'يجيب المشارك بنعم أو لا. لا حاجة لكتابة خيارات.',
        [SURVEY_QUESTION_TYPE.STARS]: 'يقيّم المشارك من نجمة إلى خمس نجوم. لا حاجة لكتابة خيارات.',
        [SURVEY_QUESTION_TYPE.SHORT_TEXT]: 'يكتب المشارك إجابة نصية قصيرة.',
        [SURVEY_QUESTION_TYPE.LONG_TEXT]: 'يكتب المشارك إجابة نصية مطوّلة.',
      },
      mandatory: 'سؤال إلزامي',
      mandatoryHint: 'لا يمكن إرسال الاستبيان دون الإجابة عنه',
      options: 'الخيارات',
      optionsHint: 'خياران على الأقل، كل منهما بالعربية والإنجليزية',
      optionAr: 'الخيار بالعربية',
      optionEn: 'الخيار بالإنجليزية',
      addOption: 'إضافة خيار',
      removeOption: 'حذف الخيار',
      addQuestion: 'إضافة سؤال',
      removeQuestion: 'حذف السؤال',
      noQuestions: 'لا توجد أسئلة بعد',
      noQuestionsYet: 'أضف سؤالاً واحداً على الأقل قبل الحفظ',
      import: 'استيراد من ملف Excel',
      importTitle: 'رفع ملف الأسئلة',
      importNotes:
        'حمّل القالب واملأ صفاً لكل سؤال: السؤال بالعربية والإنجليزية، إلزامي (0 أو 1)، نوع السؤال (1 إلى 6)، ثم — لأسئلة الاختيار الواحد والمتعدد — الخيارات بالأزواج، عربي ثم إنجليزي. الصفوف غير المطابقة تُعرض بعد الرفع.',
      imported: (count) => `تمت إضافة ${count} من الأسئلة من الملف`,
      importedNone: 'تعذّر قراءة أي سؤال من الملف',
      skippedTitle: (count) => `${count} من الصفوف لم تُقبل`,
      skippedRow: (row) => `الصف ${row}`,
      dismiss: 'إخفاء',
    },
    nameAr: 'العنوان بالعربية',
    descriptionAr: 'الوصف باللغة العربية',
    nameEn: 'العنوان بالإنجليزية',
    descriptionEn: 'الوصف باللغة الإنجليزية',
    enabled: 'هذا الاستبيان نشط',
    type: 'نوع الاستبيان', // TableHead_9
    course: 'الدورة التدريبية',
    program: 'البرنامج التدريبي',
    chooseTarget: 'اختر من القائمة',
    restrictToGroups: 'تخصيص هذا الاستبيان لأقسام',
    restrictHint: 'عند التفعيل لن يظهر الاستبيان إلا للأقسام المختارة',
    groups: 'الأقسام المختارة',
    selectAll: 'اختيار الكل',
    filter: 'تصفية',
    mandatory: 'إجبار المستخدمين على اخذ هذا الاستبيان',
    restriction: 'طريقة دخول الاستبيان',
    /* The window. `IsTimeRestricted`, `StartDate` and `EndDate` were mapped in both
       directions all along and no control ever set them — so the server could enforce a
       period the panel had no way to enter. */
    timeRestricted: 'تحديد فترة زمنية للاستبيان',
    timeRestrictedHint: 'عند التفعيل لن يظهر الاستبيان خارج الفترة المحددة',
    period: 'الفترة',
    from: 'من',
    to: 'إلى',
    restrictions: {
      [SURVEY_RESTRICTION_METHOD.COURSE_AFTER_WATCHING_CONTENT]: 'بعد مشاهدة المحتوى',
      [SURVEY_RESTRICTION_METHOD.COURSE_AFTER_TAKING_EXAM]: 'بعد إنهاء الاختبار',
      [SURVEY_RESTRICTION_METHOD.PROGRAM_AFTER_FINISHING_ALL_COURSES]: 'بعد الإنتهاء من مشاهدة كافة الدورات',
      [SURVEY_RESTRICTION_METHOD.PROGRAM_AFTER_FINISHING_ALL_COURSES_SUCCESSFULLY]: 'بعد الإنتهاء من كافة الاختبارات',
    },
    next: 'التالي',
    back: 'السابق',
    save: 'حفظ',
    required: 'الحقل مطلوب',
  },


  /** تفاصيل الاستبيان — `GetSurveyReport`. */
  report: {
    title: 'تفاصيل الاستبيان', // btnDetails
    total: (n) =>
      n === 0 ? 'لم يجب أحد على هذا الاستبيان بعد' : `إجمالي الإجابات: ${n}`,
    noAnswers: 'لا توجد إجابات على هذا السؤال',
    yes: 'نعم',
    no: 'لا',
    empty: 'لا توجد أسئلة في هذا الاستبيان',
    emptyHint: 'أضف أسئلة للاستبيان لتظهر نتائجها هنا.',
    /* `DownloadSurveyReport` has always answered and nothing ever called it, so the figures
       could be read here and not taken away. It returns a CSV. */
    download: 'تنزيل التقرير',
    downloadFailed: 'تعذر تنزيل التقرير',
  },

  deleteConfirm: 'هل تريد حذف هذا الاستبيان؟',
  deleted: 'تمت العملية بنجاح', // Operation_Completed_Successfully
}

export const assessmentsStrings = {
  sections: { list: 'القياسات', settings: 'الإعدادات' },
  ...shared,
  /* Names the operation rather than "the operation": the server answers every one of
     these with the same line, so the screen has to say which call it made. */
  fail: {
    list: 'تعذر تحميل الاختبارات',
    save: 'تعذر حفظ الاختبار',
    delete: 'تعذر حذف الاختبار',
    report: 'تعذر تحميل تقرير الاختبار',
  },
  title: 'إدارة القياسات', // AssessmentsManagement.PanelHeading
  subtitle: 'إضافة أو تعديل أو حذف القياسات', // PanelSubHeading
  create: 'إضافة قياس جديد', // MainPanelTitle
  enableFeature: 'تفعيل خاصية القياس', // LocChkBxEnable

  columns: {
    index: 'م', // TableHead_1
    title: 'العنوان', // TableHead_2
    startDate: 'تاريخ البداية', // TableHead_3
    endDate: 'تاريخ النهاية', // TableHead_4
    status: 'الحالة', // TableHead_5
    mandatory: 'الإلزامية', // TableHead_6
    passed: 'عدد الناجحين', // TableHead_7
    failed: 'عدد الراسبين', // TableHead_8
    absent: 'عدد الغائبين', // TableHead_9
  },

  // GetAssessmentsListHandler.cs:22-25 — مفعل/معطل and إلزامي/غير إلزامي.
  active: 'مفعل',
  inactive: 'معطل',
  yes: 'إلزامي',
  no: 'غير إلزامي',

  row: {
    update: 'تعديل', // btnUpdate
    details: 'تقارير القياس', // btnDetails
    sendEmail: 'إرسال بريد', // btnSendEmail
    delete: 'حذف', // btnDelete
  },

  /**
   * تقارير القياس — the screen `row.details` opens.
   *
   * The tab names are the legacy exam report's own (Locale/Admin/Exams.resx), because that
   * is the screen its row action borrows; the rest is new, since the legacy has nothing
   * that names an assessment's audience.
   */
  report: {
    title: 'تقرير القياس',
    /* A report URL with an id nobody owns used to draw the whole report with every figure
       at zero, which reads exactly like a real assessment nobody has sat. */
    notFound: 'لا يوجد قياس بهذا الرقم',
    notFoundHint: 'تحقق من الرابط، أو ارجع إلى قائمة القياسات.',
    backToList: 'قائمة القياسات',
    passMark: 'درجة النجاح',
    questionsCount: 'عدد الأسئلة',
    searchUsers: 'ابحث بالاسم أو البريد',
    tabs: {
      passed: 'الناجحون', // Exams.label1Resource1_Text
      failed: 'الراسبون', // Exams.label3Resource1_Text
      unattended: 'لم يحضروا', // Exams.unattendedListHeading_Text
      answers: 'مستوى الإجابات', // Exams.label5Resource1_Text
    },
    stats: {
      audience: 'المستهدفون',
      passed: 'نجحوا',
      failed: 'رسبوا',
      unattended: 'لم يحضروا',
    },
    columns: {
      index: 'م',
      name: 'الاسم',
      userName: 'اسم المستخدم',
      email: 'البريد الإلكتروني',
      department: 'الإدارة',
      employeeId: 'الرقم الوظيفي',
      examDate: 'تاريخ الاختبار',
      score: 'النتيجة',
      question: 'السؤال',
      category: 'الموضوع',
      difficulty: 'مستوى الصعوبة',
      answers: 'عدد الإجابات',
      correct: 'نسبة الإجابات الصحيحة',
    },
    empty: {
      passed: 'لم ينجح أحد في هذا القياس بعد',
      failed: 'لم يرسب أحد في هذا القياس',
      unattended: 'لم يتبق أحد — أدى المستهدفون جميعهم القياس',
      answers: 'لم تسجل إجابات على هذا القياس بعد',
    },
  },
  /**
   * The قياس form — Views/AssessmentsManagement `#mdlAssessment`.
   *
   * `identity` / `schedule` / `scope` are section headings, not legacy labels:
   * the legacy is one undivided column of fourteen controls, and the three
   * things an assessment actually is had no names of their own.
   */
  form: {
    groups: 'تخصيص القياس للإدارات والمجموعات التالية',
    departments: 'الإدارات',
    specialGroups: 'المجموعات الخاصة',
    groupsEmpty: 'لا توجد إدارة أو مجموعة بهذا الاسم',
    identity: 'التعريف',
    schedule: 'الفترة والتقدير',
    scope: 'النطاق',
    passHint: 'النسبة المئوية المطلوبة لاجتياز القياس',
    nameAr: 'العنوان بالعربية',
    descriptionAr: 'الوصف باللغة العربية',
    nameEn: 'العنوان بالإنجليزية',
    descriptionEn: 'الوصف باللغة الإنجليزية',
    startDate: 'تاريخ البداية', // TableHead_3
    endDate: 'تاريخ النهاية', // TableHead_4
    enabled: 'هذا القياس نشط',
    mandatory: 'هذا القياس إلزامي',
    questionsCount: 'عدد الأسئلة',
    passLimit: 'نسبة النجاح',
    publicity: 'التخصيص',
    public: 'عام', // CategoriesManagement.Public
    private: 'خاص', // CategoriesManagement.Private
    categories: 'تخصيص المواضيع',
    selectAll: 'اختيار الكل',
    filter: 'تصفية',
    save: 'حفظ',
    required: 'الحقل مطلوب',
    /* Word for word what SaveAssessmentHandler answers, so the step that holds the field
       says the same thing the save would otherwise have said once the whole form was
       filled in. */
    endBeforeStart: 'يجب ألا يسبق تاريخ النهاية تاريخ البداية',
    questionsRange: 'عدد الأسئلة يجب أن يكون بين 2 و 1000',
    passRange: 'حد النجاح يجب أن يكون بين 1 و 100',
  },

  deleteConfirm: 'هل تريد حذف هذا القياس؟',
  deleted: 'تمت العملية بنجاح', // Operation_Completed_Successfully
}
